import axios from 'axios';
import { User } from '../types';
import { compressAvatarImage, compressBackgroundImage } from '../utils/imageCompression';

// Runtime detection of production API URL
let API_BASE_URL: string;

// API URL detection - use env var or fallback to relative URLs (same origin)
if (process.env.REACT_APP_API_BASE_URL) {
  API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
} else {
  // Use relative URLs (empty string) - works for both dev proxy and production
  API_BASE_URL = '';
}

// Create axios instance with default config
const api = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true, // Important for cookies
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token if available
api.interceptors.request.use(
  (config) => {
    // Token is handled via cookies, but we can add additional headers if needed
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle common errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    const url = error.config?.url || '';
    const isAuthCall = url.includes('/signin') || url.includes('/signup') || url.includes('/forgot-password');
    const alreadyOnAuth = window.location.pathname.includes('/auth');
    // Only redirect to login on 401 for non-auth API calls when not already on auth page
    if (error.response?.status === 401 && !isAuthCall && !alreadyOnAuth) {
      window.location.href = '/auth';
    }
    return Promise.reject(error);
  }
);

export interface LoginData {
  email: string;
  password: string;
}

export interface SignUpData {
  email: string;
  password: string;
  displayName: string;
}

export interface ApiResponse<T> {
  user: ApiResponse<{ user: User; token: string; }>;
  success: boolean;
  data?: T;
  message?: string;
}

// Authentication APIs
export const syncPublicKey = async (publicKey: string) => {
  return api.put('/api/auth/user/public-key', { publicKey });
};

export const login = async (data: LoginData) => {
  const response = await api.post<ApiResponse<{ user: User; token: string }>>('/api/auth/signin', data);

  // Initialize or update encryption keys after successful login
  if (response.data.success) {
    const { initializeEncryption } = await import('../services/encryption');
    const publicKey = initializeEncryption();

    // Upload public key to server
    try {
      await api.put('/api/auth/user/public-key', { publicKey });
    } catch (error) {
      console.warn('Failed to upload public key:', error);
    }
  }

  return response;
};

export const signUp = async (data: SignUpData) => {
  // Cast as any to accommodate the pendingApproval shape
  const response = await api.post<any>('/api/auth/signup', data);
  return response;
};

export const logout = async () => {
  const response = await api.get<ApiResponse<null>>('/api/auth/logout');

  // Clear encryption keys on logout
  if (response.data.success) {
    const { clearKeys } = await import('../services/encryption');
    clearKeys();
  }

  return response;
};

export const getCurrentUser = async () => {
  return api.get<ApiResponse<{ user: User }>>('/api/auth/current/user');
};

// Profile APIs
export const updateProfile = async (updates: Partial<User>) => {
  console.log('userApi: Sending profile update to backend:', JSON.stringify(updates, null, 2));
  return api.put<ApiResponse<{ user: User }>>('/api/auth/profile', updates);
};

// Family APIs (to be expanded later)
export const getFamilyMembers = async () => {
  return api.get<ApiResponse<{ members: User[] }>>('/api/auth/family/members');
};

// Admin APIs
export const uploadBackgroundImage = async (file: File) => {
  console.log('Background upload: Starting compression...');
  const compressedFile = await compressBackgroundImage(file);

  console.log('Background upload: Compressed file info:', {
    name: compressedFile.name,
    type: compressedFile.type,
    size: compressedFile.size
  });

  // Ensure the compressed file has proper metadata (preserve original filename and type)
  const fileToUpload = new File([compressedFile], file.name, {
    type: file.type || 'image/jpeg',
    lastModified: Date.now()
  });

  console.log('Background upload: File to upload info:', {
    name: fileToUpload.name,
    type: fileToUpload.type,
    size: fileToUpload.size
  });

  const formData = new FormData();
  formData.append('background', fileToUpload);

  return api.post<ApiResponse<{ backgroundUrl: string }>>('/api/auth/admin/upload-background', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
};

export const getBackgroundImage = async () => {
  return api.get<ApiResponse<{ backgroundUrl: string }>>('/api/auth/background-image');
};

export const getAllBackgroundImages = async () => {
  console.log('userApi: Making API call to /api/auth/admin/background-images');
  console.log('userApi: API base URL:', API_BASE_URL);
  try {
    const response = await api.get<ApiResponse<{ backgrounds: BackgroundImage[]; currentBackground: string }>>('/api/auth/admin/background-images');
    console.log('userApi: Gallery API call successful:', response);
    return response;
  } catch (error) {
    console.error('userApi: Gallery API call failed:', error);
    throw error;
  }
};

export const setBackgroundImage = async (backgroundUrl: string) => {
  return api.post<ApiResponse<{ backgroundUrl: string }>>('/api/auth/admin/set-background', { backgroundUrl });
};

export const deleteBackgroundImage = async (filePath: string) => {
  // Encode the filePath for URL (e.g., "backgrounds/file.jpg" becomes "backgrounds%2Ffile.jpg")
  const encodedPath = encodeURIComponent(filePath);
  return api.delete<ApiResponse<{}>>(`/api/auth/admin/background-image/${encodedPath}`);
};

export interface BackgroundImage {
  filename: string;
  url: string;
  filePath: string;
  size: number;
  uploadedAt: string;
  isCurrent: boolean;
}

// CSV Import APIs
export interface ImportResult {
  total: number;
  imported: number;
  skipped: number;
  errors: string[];
}

export const importUsers = async (file: File) => {
  const formData = new FormData();
  formData.append('csvFile', file);

  return api.post<ApiResponse<ImportResult>>('/api/auth/admin/import-users', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
};

// Individual user creation
export interface CreateUserData {
  email: string;
  displayName: string;
  firstName?: string;
  lastName?: string;
  phoneNumber?: string;
  occupation?: string;
  bio?: string;
  photoURL?: string;
  birthday?: string;
  isLocationVisible?: boolean;
  isBirthdayVisible?: boolean;
}

export const createUser = async (data: CreateUserData) => {
  return api.post<ApiResponse<{ user: User }>>('/api/auth/admin/create-user', data);
};

// Delete user (Admin only)
export const deleteUser = async (userId: number) => {
  return api.delete<ApiResponse<{ deletedUser: { id: number; email: string; displayName: string } }>>(`/api/auth/admin/user/${userId}`);
};

// Promote user to manager (Admin only)
export const promoteToManager = async (userId: number) => {
  return api.post<ApiResponse<{ promotedUser: { id: number; email: string; displayName: string } }>>(`/api/auth/admin/promote-manager/${userId}`);
};

// Get all users (Admin only)
export const getAllUsers = async () => {
  return api.get<ApiResponse<{ users: User[] }>>('/api/auth/admin/users');
};

// Pending approval management (Admin/Manager only)
export const getPendingApprovals = async () => {
  return api.get<ApiResponse<{ users: User[] }>>('/api/auth/admin/pending-approvals');
};

export const approveUser = async (userId: number) => {
  return api.post<ApiResponse<null>>(`/api/auth/admin/approve-user/${userId}`);
};

export const rejectUser = async (userId: number) => {
  return api.post<ApiResponse<null>>(`/api/auth/admin/reject-user/${userId}`);
};

// Device management
export const getMyDevices = async () => {
  return api.get<any>('/api/devices');
};

export const deleteDevice = async (deviceId: string) => {
  return api.delete<any>(`/api/devices/${encodeURIComponent(deviceId)}`);
};

// Test email (Admin only)
export const testEmail = async (email: string) => {
  return api.post<ApiResponse<{ connectionTest: any }>>('/api/auth/admin/test-email', { email });
};

// Avatar upload with compression
export const uploadAvatar = async (file: File) => {
  console.log('Avatar upload: Starting compression...');
  const compressedFile = await compressAvatarImage(file);

  const formData = new FormData();
  formData.append('avatar', compressedFile);

  return api.post<ApiResponse<{ user: User; avatarUrl: string }>>('/api/auth/upload-avatar', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
};

// Theme management
export interface ThemeData {
  colors: {
    primary: string;
    primaryLight: string;
    primaryDark: string;
    secondary: string;
    secondaryLight: string;
    secondaryDark: string;
    backgroundDefault: string;
    backgroundPaper: string;
    textPrimary: string;
    textSecondary: string;
    error: string;
    warning: string;
    info: string;
    success: string;
    borderColor: string;
    authFieldBackground: string;
  };
  typography: {
    fontFamily: string;
    fontSize: string;
    h1Size: string;
    h2Size: string;
    h3Size: string;
  };
  spacing: {
    unit: string;
    containerPadding: string;
    cardMargin: string;
  };
  layout: {
    borderRadius: string;
    borderRadiusSm: string;
    borderRadiusLg: string;
  };
  effects: {
    shadowLight: string;
    shadowMedium: string;
    shadowHeavy: string;
    glassmorphism: boolean;
    darkMode: boolean;
    highContrast: boolean;
  };
}

export const saveTheme = async (theme: ThemeData) => {
  return api.post<ApiResponse<{ message: string }>>('/api/auth/admin/theme', { theme });
};

export const getTheme = async () => {
  return api.get<ApiResponse<{ theme: ThemeData | null }>>('/api/auth/admin/theme');
};

// User personal theme preferences
export interface UserThemePreferences {
  usePersonalTheme: boolean;
  personalTheme: ThemeData | null;
  personalBackgroundUrl: string | null;
}

export const getUserThemePreferences = async () => {
  return api.get<ApiResponse<UserThemePreferences>>('/api/auth/user/theme-preferences');
};

export const togglePersonalTheme = async (usePersonalTheme: boolean) => {
  return api.put<ApiResponse<null>>('/api/auth/user/theme-preferences/toggle', { usePersonalTheme });
};

export const savePersonalTheme = async (theme: ThemeData) => {
  return api.put<ApiResponse<null>>('/api/auth/user/theme-preferences/theme', { theme });
};

export const uploadPersonalBackground = async (file: File) => {
  const formData = new FormData();
  formData.append('background', file);
  return api.post<ApiResponse<{ backgroundUrl: string }>>('/api/auth/user/theme-preferences/background', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
};

// Email verification APIs
export const verifyEmail = async (token: string, email: string) => {
  return api.post<ApiResponse<null>>('/api/auth/verify-email', { token, email });
};

export const resendVerificationEmail = async (email: string) => {
  return api.post<ApiResponse<null>>('/api/auth/resend-verification', { email });
};

// Avatar management
export const refreshAvatarUrl = async () => {
  return api.post<ApiResponse<{ avatarUrl: string | null }>>('/api/auth/refresh-avatar-url');
};

export const fixAvatarUrl = async () => {
  return api.post<ApiResponse<{ avatarUrl: string }>>('/api/auth/fix-avatar');
};

// Location APIs
export interface LocationData {
  latitude: number;
  longitude: number;
  accuracy?: number;
}

export const updateLocation = async (location: LocationData) => {
  return api.post<ApiResponse<null>>('/api/auth/location/update', location);
};

export const clearLocation = async () => {
  return api.delete<ApiResponse<null>>('/api/auth/location/clear');
};

// Notification APIs
export const getNewMemberCount = async () => {
  return api.get<ApiResponse<{ count: number }>>('/api/auth/admin/new-member-count');
};

export default api;